import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, thumbUrl } from "@/lib/api";
import EventsMap from "@/components/EventsMap";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const monthKey = (iso) => {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  } catch {
    return "";
  }
};

const monthLabel = (key) => {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();

  // Filter state (seeded from URL so the homepage search can deep-link here)
  const [search, setSearch] = useState(params.get("q") || "");
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [sort, setSort] = useState("date-desc"); // date-desc | date-asc | track-asc | photos-desc
  const [view, setView] = useState("grid"); // grid | list

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/events");
        if (!cancelled) setEvents(data || []);
      } catch (e) {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const fromDate = from ? new Date(from) : null;
    const toDate = to ? new Date(to) : null;
    let list = events.filter((ev) => {
      if (term) {
        const hay = `${ev.track || ""} ${ev.name || ""} ${ev.description || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (fromDate && new Date(ev.date) < fromDate) return false;
      if (toDate && new Date(ev.date) > toDate) return false;
      return true;
    });
    if (sort === "date-desc") list = list.sort((a, b) => new Date(b.date) - new Date(a.date));
    if (sort === "date-asc") list = list.sort((a, b) => new Date(a.date) - new Date(b.date));
    if (sort === "track-asc") list = list.sort((a, b) => (a.track || "").localeCompare(b.track || ""));
    if (sort === "photos-desc") list = list.sort((a, b) => (b.photo_count || 0) - (a.photo_count || 0));
    return list;
  }, [events, search, from, to, sort]);

  const grouped = useMemo(() => {
    // Group by month for the list view — feels calendar-like without a real calendar widget.
    const map = new Map();
    for (const ev of filtered) {
      const key = monthKey(ev.date);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const clearFilters = () => {
    setSearch(""); setFrom(""); setTo(""); setSort("date-desc");
  };
  const hasFilters = search || from || to || sort !== "date-desc";

  return (
    <div className="min-h-screen bg-black text-white" data-testid="events-page">
      {/* Hero header */}
      <section className="border-b-2 border-[var(--pm-orange-40)]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-16 pb-10">
          <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">Race calendar</p>
          <h1 className="font-display text-5xl md:text-7xl uppercase mt-2">Events</h1>
          <p className="text-sm text-white/50 mt-4 max-w-2xl font-condensed">
            Every round we&apos;ve shot. Filter by track, name or date to jump straight to the classes you rode.
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 mt-10">
        <div className="border border-white/10 bg-[#0a0a0a] p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-end" data-testid="events-filter-bar">
          <div className="md:col-span-4">
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-2">Search</label>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Foxhill, Round 1, junior…"
              className="input-minimal w-full"
              data-testid="events-search"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-2">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="input-minimal w-full"
              data-testid="events-date-from"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-2">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="input-minimal w-full"
              data-testid="events-date-to"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-2">Sort</label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="input-minimal w-full bg-transparent"
              data-testid="events-sort"
            >
              <option value="date-desc">Newest first</option>
              <option value="date-asc">Oldest first</option>
              <option value="track-asc">Track A–Z</option>
              <option value="photos-desc">Most photos</option>
            </select>
          </div>
          <div className="md:col-span-2 flex gap-2 justify-end">
            <button
              onClick={() => setView("grid")}
              className={`text-xs uppercase tracking-[0.3em] px-4 py-3 border ${view === "grid" ? "bg-[var(--pm-orange)] text-black border-[var(--pm-orange)] font-semibold" : "border-white/20 hover:border-white/60"}`}
              data-testid="events-view-grid"
            >
              Grid
            </button>
            <button
              onClick={() => setView("list")}
              className={`text-xs uppercase tracking-[0.3em] px-4 py-3 border ${view === "list" ? "bg-[var(--pm-orange)] text-black border-[var(--pm-orange)] font-semibold" : "border-white/20 hover:border-white/60"}`}
              data-testid="events-view-list"
            >
              List
            </button>
            <button
              onClick={() => setView("map")}
              className={`text-xs uppercase tracking-[0.3em] px-4 py-3 border ${view === "map" ? "bg-[var(--pm-orange)] text-black border-[var(--pm-orange)] font-semibold" : "border-white/20 hover:border-white/60"}`}
              data-testid="events-view-map"
            >
              Map
            </button>
          </div>
        </div>

        <div className="flex items-baseline justify-between mt-4 text-xs font-condensed text-white/60">
          <span data-testid="events-count">
            <span className="pm-orange font-semibold">{filtered.length}</span> of {events.length} {events.length === 1 ? "event" : "events"}
          </span>
          {hasFilters && (
            <button onClick={clearFilters} className="uppercase tracking-[0.3em] text-white/60 hover:text-white" data-testid="events-clear-filters">
              Clear filters
            </button>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 mt-8 pb-24">
        {loading ? (
          <p className="text-center py-24 text-white/40 text-sm uppercase tracking-[0.3em]">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24" data-testid="events-empty">
            <p className="pm-orange text-sm uppercase tracking-[0.3em] mb-4 font-semibold">
              {events.length === 0 ? "No events listed yet" : "No events match those filters"}
            </p>
            <p className="text-white/50 text-sm">
              {events.length === 0 ? "Check back after the next race weekend." : "Try widening the date range or clearing the search."}
            </p>
          </div>
        ) : view === "map" ? (
          <EventsMap events={filtered} />
        ) : view === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
            {filtered.map((ev, idx) => (
              <Link
                key={ev.id}
                to={`/event/${ev.id}`}
                className="pm-card block aspect-[4/3] fade-up"
                style={{ animationDelay: `${idx * 40}ms` }}
                data-testid={`event-card-${ev.id}`}
              >
                {ev.cover_image_id ? (
                  <img src={thumbUrl(ev.cover_image_id)} alt={ev.track} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#111]" />
                )}
                <div className="absolute top-4 left-4 pm-slash text-xs">
                  <span>{fmtDate(ev.date)}</span>
                </div>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent pt-16 pb-5 px-5 pointer-events-none">
                  <h3 className="font-display text-2xl md:text-3xl uppercase text-white leading-tight">{ev.track}</h3>
                  {ev.name && <p className="text-sm text-white/70 mt-1 font-condensed">{ev.name}</p>}
                  <p className="text-xs uppercase tracking-[0.2em] pm-orange mt-2 font-semibold">
                    {ev.group_count} {ev.group_count === 1 ? "class" : "classes"} · {ev.photo_count} photos
                  </p>
                </div>
                <div className="reveal">
                  <div />
                  <span className="text-xs uppercase tracking-[0.2em] text-black bg-[var(--pm-orange)] px-3 py-2 font-bold">Open</span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="space-y-10" data-testid="events-list">
            {grouped.map(([mkey, items]) => (
              <div key={mkey}>
                <div className="flex items-baseline gap-4 mb-4 pb-2 border-b border-white/10">
                  <h3 className="font-display text-2xl uppercase pm-orange">{monthLabel(mkey)}</h3>
                  <span className="text-xs uppercase tracking-[0.3em] text-white/40 font-condensed">
                    {items.length} {items.length === 1 ? "round" : "rounds"}
                  </span>
                </div>
                <ul>
                  {items.map((ev) => (
                    <li key={ev.id} className="border-b border-white/5">
                      <Link
                        to={`/event/${ev.id}`}
                        className="grid grid-cols-12 gap-4 items-center py-5 px-2 hover:bg-white/[0.03] transition-colors group"
                        data-testid={`event-row-${ev.id}`}
                      >
                        <div className="col-span-2 md:col-span-2">
                          {ev.cover_image_id ? (
                            <img src={thumbUrl(ev.cover_image_id)} alt={ev.track} className="w-full aspect-square object-cover" loading="lazy" />
                          ) : (
                            <div className="w-full aspect-square bg-[#111]" />
                          )}
                        </div>
                        <div className="col-span-2 md:col-span-2 font-condensed text-white/70 text-sm uppercase tracking-[0.2em]">
                          {fmtDate(ev.date)}
                        </div>
                        <div className="col-span-8 md:col-span-6">
                          <h4 className="font-display text-xl md:text-2xl uppercase leading-tight group-hover:pm-orange transition-colors">{ev.track}</h4>
                          {ev.name && <p className="text-sm text-white/50 mt-1 font-condensed">{ev.name}</p>}
                        </div>
                        <div className="col-span-12 md:col-span-2 text-xs uppercase tracking-[0.2em] text-white/50 font-condensed md:text-right">
                          {ev.group_count || 0} classes · {ev.photo_count || 0} photos
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
